// AP Cart Drawer — custom element that handles open/close, qty +/-,
// remove, and uses Shopify Section Rendering API to re-render in place.
//
// Registered as <ap-cart-drawer>. Triggered by:
//   - Add-to-cart success on PDP (ap-product-main.liquid)
//   - Click on the header cart icon (link to /cart)
//   - Esc key / backdrop click closes

class APCartDrawer extends HTMLElement {
  constructor() {
    super();
    this._isOpen = false;
    this._lastFocus = null;
  }

  connectedCallback() {
    this.bindCloseHandlers();
    this.bindHeaderTrigger();
    this.bindLineActions();
  }

  bindCloseHandlers() {
    // (1) Delegated click on host — catches × button + overlay
    // when the overlay's pointer-events: auto is working.
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]')) this.close();
    });

    // (2) BULLETPROOF FALLBACK — document-level outside-click.
    // The CSS rule .ap-cart-drawer.is-open .ap-cart-drawer__overlay
    // { pointer-events: auto } can fail to apply when the section
    // is re-rendered or class timing is off, which makes clicks
    // pass straight THROUGH the overlay to the page below — then
    // they never reach (1) and the drawer can only be closed by ×.
    //
    // This listener lives on document and never dies. It fires
    // for ANY click while the drawer is open and closes when the
    // click is not inside the panel and not on a trigger.
    document.addEventListener('click', (e) => {
      if (!this._isOpen) return;
      const panel = this.querySelector('.ap-cart-drawer__panel');
      if (!panel) return;
      if (panel.contains(e.target)) return;  // click inside card — keep open
      // Allow click on cart-icon trigger to not insta-close.
      if (e.target.closest('a#cart-icon-bubble, [data-open-cart]')) return;
      this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._isOpen) this.close();
    });
  }

  bindHeaderTrigger() {
    // Intercept the header cart icon link → open drawer instead of navigating
    document.querySelectorAll('a#cart-icon-bubble, [data-open-cart]').forEach(link => {
      link.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // allow open-in-new-tab
        e.preventDefault();
        this.open(link);
      });
    });
  }

  bindLineActions() {
    this.addEventListener('click', (e) => {
      const qtyBtn = e.target.closest('[data-qty-change]');
      if (qtyBtn) {
        const dir = qtyBtn.dataset.qtyChange;
        const line = qtyBtn.closest('[data-cart-line]');
        if (!line) return;
        const key = line.dataset.cartLine;
        const num = parseInt(line.querySelector('.ap-cart-drawer__qty-num').textContent, 10);
        const next = dir === 'inc' ? num + 1 : num - 1;
        this.changeLine(key, Math.max(0, next), qtyBtn);
        return;
      }
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        const line = removeBtn.closest('[data-cart-line]');
        if (line) this.changeLine(line.dataset.cartLine, 0, removeBtn);
      }
    });
  }

  open(trigger) {
    this._lastFocus = trigger || document.activeElement;
    this.classList.add('is-open');
    document.body.classList.add('ap-cart-drawer-open');
    this._isOpen = true;
    // Focus the panel for screen readers / keyboard nav
    const panel = this.querySelector('.ap-cart-drawer__panel');
    if (panel) setTimeout(() => panel.focus(), 50);
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('ap-cart-drawer-open');
    this._isOpen = false;
    if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
  }

  // ---- Silky qty update -------------------------------------------------
  // On +/- we DON'T replace the panel HTML (that caused the flicker / image
  // reload). Instead we update the line qty, line price, subtotal and
  // free-ship bar in place with a crossfade, then sync the server in the
  // background (debounced). The server response only forces a full re-render
  // if the truth diverged (stock clamp, free-ship threshold crossed, or cart
  // emptied) — otherwise the change stays perfectly smooth.

  esc(key) { return (window.CSS && CSS.escape) ? CSS.escape(key) : key; }

  money(cents) {
    return '$' + (Math.round(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  setAmt(el, cents) {
    if (!el) return;
    el.classList.add('is-amt-changing');
    setTimeout(() => { el.textContent = this.money(cents); el.classList.remove('is-amt-changing'); }, 150);
  }

  lineQty(line) {
    const n = line && line.querySelector('.ap-cart-drawer__qty-num');
    return n ? (parseInt(n.textContent, 10) || 0) : 0;
  }

  isUnlocked() {
    return !!this.querySelector('.ap-cart-drawer__shipping.is-unlocked, .ap-cart-drawer__shipping-success');
  }

  recomputeTotals() {
    let subtotal = 0, count = 0;
    this.querySelectorAll('.ap-cart-drawer__item:not(.is-removing)').forEach((li) => {
      const unit = parseInt(li.dataset.unitPrice, 10) || 0;
      const q = this.lineQty(li);
      subtotal += unit * q;
      count += q;
    });
    this.setAmt(this.querySelector('[data-cart-subtotal]'), subtotal);
    const cc = this.querySelector('[data-cart-count]');
    if (cc) cc.textContent = count;
    this.updateHeaderBadge(count);
    const thr = parseInt(this.getAttribute('data-free-ship-threshold'), 10) || 0;
    if (thr) {
      const strong = this.querySelector('.ap-cart-drawer__shipping-msg strong');
      if (strong) this.setAmt(strong, Math.max(0, thr - subtotal));
      const fill = this.querySelector('.ap-cart-drawer__bar-fill');
      if (fill) fill.style.width = Math.min(100, Math.round(subtotal * 100 / thr)) + '%';
    }
    return subtotal;
  }

  changeLine(key, quantity, sourceEl) {
    const line = this.querySelector('[data-cart-line="' + this.esc(key) + '"]');
    if (!line) { this.queueSync(key, Math.max(0, quantity)); return; }

    if (quantity <= 0) {
      line.classList.add('is-removing');
      let removed = false;
      const drop = () => {
        if (removed) return;
        removed = true;
        line.remove();
        this.recomputeTotals();
        this.queueSync(key, 0);
      };
      line.addEventListener('transitionend', drop, { once: true });
      setTimeout(drop, 360); // fallback if transitionend never fires
      return;
    }

    const unit = parseInt(line.dataset.unitPrice, 10) || 0;
    const numEl = line.querySelector('.ap-cart-drawer__qty-num');
    if (numEl) numEl.textContent = quantity;
    this.setAmt(line.querySelector('.ap-cart-drawer__item-price'), unit * quantity);
    this.recomputeTotals();
    this.queueSync(key, quantity);
  }

  queueSync(key, quantity) {
    this._pending = this._pending || {};
    this._pending[key] = quantity;
    clearTimeout(this._syncTimer);
    this._syncTimer = setTimeout(() => this.flushSync(), 350);
  }

  flushSync() {
    if (this._syncing) return;
    const updates = this._pending || {};
    const keys = Object.keys(updates);
    if (!keys.length) return;
    this._pending = {};
    this._syncing = true;
    const wasUnlocked = this.isUnlocked();

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates, sections: this.sectionsToRender(), sections_url: window.location.pathname }),
    })
      .then((r) => { if (!r.ok) throw new Error('update ' + r.status); return r.json(); })
      .then((cart) => {
        this._syncing = false;
        this.updateHeaderBadge(cart.item_count);

        // STILL CLICKING: more changes are queued, so THIS response is already
        // stale. Do NOT reconcile/re-render now — that would snap the number
        // back to an old value (the "+ to 4 rebounds to 3" bug). Let the final
        // flush settle the truth.
        if (Object.keys(this._pending || {}).length) { this.flushSync(); return; }

        const thr = parseInt(this.getAttribute('data-free-ship-threshold'), 10) || 0;
        const nowUnlocked = thr > 0 && cart.total_price >= thr;

        // Settled — did server truth diverge from our optimistic render?
        let diverged = cart.item_count === 0 || nowUnlocked !== wasUnlocked;
        if (!diverged) {
          for (let i = 0; i < keys.length; i++) {
            const srv = (cart.items || []).find((it) => it.key === keys[i]);
            const dom = this.querySelector('[data-cart-line="' + this.esc(keys[i]) + '"]');
            if ((dom ? this.lineQty(dom) : 0) !== (srv ? srv.quantity : 0)) { diverged = true; break; }
          }
        }

        if (diverged) {
          this.applySections(cart.sections || {}); // one clean re-render to truth (rare)
        } else if (typeof cart.total_price === 'number') {
          this.setAmt(this.querySelector('[data-cart-subtotal]'), cart.total_price); // cent-perfect, no flicker
        }
      })
      .catch((err) => {
        this._syncing = false;
        console.error('AP cart update failed:', err);
        this.hardResync(); // safety: pull truth + re-render so cart is never wrong
      });
  }

  hardResync() {
    fetch(window.location.pathname + '?sections=' + encodeURIComponent(this.sectionsToRender()), { headers: { 'Accept': 'application/json' } })
      .then((r) => r.json())
      .then((sections) => this.applySections(sections))
      .catch(() => {});
  }

  sectionsToRender() {
    // Only re-render the drawer itself. We update the header badge
    // separately via updateHeaderBadge() because requesting
    // `cart-icon-bubble` as a section would overwrite our
    // <a id="cart-icon-bubble"> link's innerHTML with Dawn's
    // section markup, destroying our SVG + badge wrap structure
    // and orphaning the badge to top-left of viewport.
    const id = this.getAttribute('data-section-id');
    return [id].filter(Boolean).join(',');
  }

  updateHeaderBadge(count) {
    const badge = document.querySelector('.ap-header__cart-badge');
    if (badge) badge.textContent = count;
  }

  applySections(sections) {
    // Replace ONLY the panel's inner content — never the <ap-cart-drawer> host.
    // Previously this replaced the whole matched section's innerHTML; when the
    // section is wrapped in #shopify-section-… that match is the wrapper, so
    // replacing its innerHTML destroyed and recreated the <ap-cart-drawer>
    // element itself, wiping its `is-open` state and making the drawer snap
    // shut on every qty +/- change. Keeping the host alive preserves the open
    // state, the delegated click/close handlers, and focus.
    Object.keys(sections).forEach(id => {
      const fresh = new DOMParser().parseFromString(sections[id], 'text/html');
      const freshPanel = fresh.querySelector('.ap-cart-drawer__panel');
      const curPanel = this.querySelector('.ap-cart-drawer__panel');
      if (freshPanel && curPanel) curPanel.innerHTML = freshPanel.innerHTML;
      // Mirror empty / non-empty state from the freshly rendered markup.
      const freshHost = fresh.querySelector('ap-cart-drawer');
      if (freshHost) this.classList.toggle('is-empty', freshHost.classList.contains('is-empty'));
    });
    // bindLineActions + close handlers are delegated on the host (this), which
    // is preserved above, so they survive the innerHTML swap — no re-bind needed.
  }
}

customElements.define('ap-cart-drawer', APCartDrawer);
